import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ReceiptDto, ReceiptFilters } from '../../types/receipt';
import { receiptService } from '../../services/receiptService';
import { formatDate, formatCurrency } from '../../utils/formatters';
import DataTable, { TableColumn, TableAction } from '../../components/UI/DataTable';
import Button from '../../components/UI/Button';
import { PlusIcon, MagnifyingGlassIcon, FunnelIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import ReceiptViewer from '../../components/Receipts/ReceiptViewer';
import Modal from '../../components/UI/Modal';


const ReceiptsPage: React.FC = () => {
  const [selectedReceipt, setSelectedReceipt] = useState<ReceiptDto | null>(null);
  const [filters, setFilters] = useState<ReceiptFilters>({
    page: 1,
    limit: 10,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [isViewerOpen, setIsViewerOpen] = useState(false);


  // Fetch receipts
  const {
    data: receiptsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['receipts', filters, searchTerm],
    queryFn: () => receiptService.getReceipts({
      ...filters,
      search: searchTerm || undefined,
    }),
  });

  // Handle filter changes
  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters(prev => ({ ...prev, page: 1 }));
  };

  // Handle receipt actions
  const handleViewReceipt = (receipt: ReceiptDto) => {
    setSelectedReceipt(receipt);
    setIsViewerOpen(true);
  };

  const handleDownloadReceipt = async (id: string) => {
    try {
      await receiptService.downloadReceipt(id);
    } catch (error) {
      console.error('Error downloading receipt:', error);
      // Handle error (e.g., show toast)
    }
  };

  const handlePrintReceipt = async (id: string) => {
    try {
      await receiptService.printReceipt(id);
    } catch (error) {
      console.error('Error printing receipt:', error);
      // Handle error (e.g., show toast)
    }
  };

  // Table columns
  const columns: TableColumn<ReceiptDto>[] = [
    {
      key: 'receiptNumber',
      header: 'Receipt #',
      sortable: true,
      width: '150px',
    },
    {
      key: 'payment.client',
      header: 'Client',
      render: (_, receipt) => (
        <div>
          <div className="font-medium">
            {receipt.payment.client.firstName} {receipt.payment.client.lastName}
          </div>
          {receipt.payment.client.email && (
            <div className="text-sm text-gray-500">{receipt.payment.client.email}</div>
          )}
        </div>
      ),
    },
    {
      key: 'payment.amount',
      header: 'Amount',
      sortable: true,
      render: (_, receipt) => (
        <div className="font-medium">
          {formatCurrency(receipt.payment.amount, receipt.payment.currency)}
        </div>
      ),
    },
    {
      key: 'issuedAt',
      header: 'Issued On',
      sortable: true,
      render: (date) => formatDate(date, 'short'),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (status) => (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            status === 'ACTIVE'
              ? 'bg-green-100 text-green-800'
              : 'bg-gray-100 text-gray-800'
          }`}
        >
          {status}
        </span>
      ),
    },
  ];

  // Table actions
  const actions: TableAction<ReceiptDto>[] = [
    {
      key: 'view',
      label: 'View',
      onClick: handleViewReceipt,
    },
    {
      key: 'download',
      label: 'Download',
      onClick: (receipt) => handleDownloadReceipt(receipt.id),
    },
    {
      key: 'print',
      label: 'Print',
      onClick: (receipt) => handlePrintReceipt(receipt.id),
    },
  ];

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Receipts</h1>
          <p className="mt-2 text-sm text-gray-700">
            A list of all receipts issued to clients.
          </p>
        </div>
        <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none">
          <Button
            onClick={() => {
              // Handle new receipt
            }}
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            New Receipt
          </Button>
        </div>
      </div>

      {/* Search and filters */}
      <div className="mt-6 flex flex-col sm:flex-row gap-4">
        <form onSubmit={handleSearch} className="flex-1">
          <div className="relative rounded-md shadow-sm">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon
                className="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </div>
            <input
              type="text"
              className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 pr-3 py-2 sm:text-sm border-gray-300 rounded-md"
              placeholder="Search receipts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </form>
        <Button variant="secondary">
          <FunnelIcon className="-ml-1 mr-2 h-5 w-5" />
          Filters
        </Button>
        <Button variant="secondary">
          <ArrowDownTrayIcon className="-ml-1 mr-2 h-5 w-5" />
          Export
        </Button>
      </div>

      {/* Receipts table */}
      <div className="mt-6">
        <DataTable
          columns={columns}
          data={receiptsData?.receipts || []}
          loading={isLoading}
          error={error ? 'Failed to load receipts' : undefined}
          actions={actions}
          getRowProps={(row) => ({
            'data-id': row.id,
            className: 'cursor-pointer hover:bg-gray-50',
            onClick: () => handleViewReceipt(row)
          })}
        />

        {/* Pagination */}
        {receiptsData?.pagination && (
          <div className="mt-4 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Showing <span className="font-medium">
                {receiptsData.pagination.page * receiptsData.pagination.limit - receiptsData.pagination.limit + 1}
              </span> to{' '}
              <span className="font-medium">
                {Math.min(
                  receiptsData.pagination.page * receiptsData.pagination.limit,
                  receiptsData.pagination.total
                )}
              </span>{' '}
              of <span className="font-medium">{receiptsData.pagination.total}</span> results
            </div>
            <div className="flex space-x-2">
              <Button
                onClick={() => handlePageChange(receiptsData.pagination.page - 1)}
                disabled={receiptsData.pagination.page === 1}
                variant="secondary"
              >
                Previous
              </Button>
              <Button
                onClick={() => handlePageChange(receiptsData.pagination.page + 1)}
                disabled={
                  receiptsData.pagination.page >= receiptsData.pagination.pages
                }
                variant="secondary"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Receipt viewer modal */}
      <Modal
        isOpen={isViewerOpen}
        onClose={() => setIsViewerOpen(false)}
        title={`Receipt #${selectedReceipt?.receiptNumber || ''}`}
        size="xl"
      >
        {selectedReceipt && (
          <div className="mt-4">
            <ReceiptViewer
              receipt={selectedReceipt}
              onPrint={() => handlePrintReceipt(selectedReceipt.id)}
              onDownload={() => handleDownloadReceipt(selectedReceipt.id)}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ReceiptsPage;
